import { BrowserProvider, JsonRpcSigner } from 'ethers'
import { getConnectorClient } from '@wagmi/core'
import type { Account, Chain, Client, Transport } from 'viem'
import { wagmiConfig } from './wagmi'

/**
 * Convert a viem Client to an ethers BrowserProvider + JsonRpcSigner.
 * This is the standard wagmi v2 pattern for ethers v6 interop.
 */
function clientToProviderSigner(client: Client<Transport, Chain, Account>) {
  const { chain, transport } = client
  // wagmi sets `chain` to undefined when the wallet's ACTIVE chain isn't in wagmiConfig.chains
  // (e.g. wallet still on Base mainnet while the app is in testnet mode). Reading `chain.id` then
  // throws a cryptic "Cannot read properties of undefined (reading 'id')". Fail with an actionable
  // message instead so the user knows to switch networks.
  if (!chain) {
    throw new Error(
      'Your wallet is on an unsupported network. Switch it to a supported network (e.g. Base Sepolia in testnet mode) and try again.'
    )
  }
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  }
  const provider = new BrowserProvider(transport, network)
  const signer = new JsonRpcSigner(provider, client.account.address)
  return { provider, signer }
}

/**
 * Imperative (non-hook) function to get an ethers provider + signer from the
 * currently connected wagmi wallet. Safe to call inside callbacks and async
 * functions — does NOT use React hooks.
 */
export async function getEthersProviderSigner(chainId?: number) {
  const client = await getConnectorClient(
    wagmiConfig,
    // Cast chainId to satisfy wagmi's strict chain ID union type
    chainId ? { chainId: chainId as any } : undefined
  )
  return clientToProviderSigner(client)
}
